import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  FileText,
  Calendar,
  ChevronRight,
  Sparkles,
  Trash2,
  Loader2
} from "lucide-react";

export default function ScriptHistory() {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, preview: null });
  const [deleting, setDeleting] = useState(false);

  const canDelete = user?.is_manager || user?.is_admin;

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(`${API}/scripts/history`, { credentials: "include" });
        if (response.ok) {
          setAnalyses(await response.json());
        }
      } catch (error) {
        toast.error("Failed to load script history");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const handleDelete = async () => {
    if (!deleteDialog.id) return;
    setDeleting(true);
    try {
      const response = await fetch(`${API}/scripts/${deleteDialog.id}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        setAnalyses(prev => prev.filter(a => a.analysis_id !== deleteDialog.id));
        toast.success("Script analysis deleted");
      } else {
        const error = await response.json();
        toast.error("Failed to delete", { description: error.detail });
      }
    } catch (error) {
      toast.error("Failed to delete script analysis");
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, id: null, preview: null });
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="script-history-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Script History</h1>
          <p className="text-muted-foreground mt-1">Review your past script analyses</p>
        </div>
        <Link to="/scripts">
          <Button className="bg-primary hover:bg-primary/90" data-testid="new-script-analysis-btn">
            <Sparkles className="w-4 h-4 mr-2" /> New Analysis
          </Button>
        </Link>
      </div>

      {analyses.length > 0 ? (
        <div className="space-y-4">
          {analyses.map((analysis) => (
            <Card
              key={analysis.analysis_id}
              className="hover:border-primary transition-colors"
              data-testid={`script-${analysis.analysis_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Link to={`/scripts/${analysis.analysis_id}`} className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-violet-500" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium truncate">
                        {analysis.script_text?.substring(0, 60)}...
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {(analysis.script_type || "script").replace(/_/g, " ")}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(analysis.created_at)}
                        </span>
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-4">
                    {analysis.score != null && (
                      <div className="text-right">
                        <p className={`font-mono text-2xl font-bold ${getScoreColor(analysis.score)}`}>
                          {analysis.score}
                        </p>
                        <p className="text-xs text-muted-foreground">score</p>
                      </div>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDeleteDialog({
                            open: true,
                            id: analysis.analysis_id,
                            preview: analysis.script_text?.substring(0, 40) + "..."
                          });
                        }}
                        data-testid={`delete-script-${analysis.analysis_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Link to={`/scripts/${analysis.analysis_id}`}>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold mb-2">No script analyses yet</h3>
          <p className="text-muted-foreground mb-4">Analyze your first sales script to get AI feedback</p>
          <Link to="/scripts">
            <Button>Analyze Script</Button>
          </Link>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Script Analysis?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this script analysis? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
